import { useEffect, useRef, useState } from 'react';
import { TabBar, type TabDef } from './components/TabBar';
import { useSession } from './hooks/useSession';
import { useThread } from './hooks/useThread';
import { useKeyboardInset } from './hooks/useKeyboardInset';
import { getToken } from './lib/api';
import { Pair } from './screens/Pair';
import { Thread } from './screens/Thread';
import { QuestionsPanel } from './screens/Questions';
import { RequestsPanel } from './screens/Requests';
import { AIPanel } from './screens/AI';
import { NotebookPanel } from './screens/Notebook';
import { Settings } from './screens/Settings';

type Tab = 'thread' | 'questions' | 'requests' | 'notebook' | 'ai';

const TABS: TabDef[] = [
  { key: 'thread', label: 'Thread' },
  { key: 'questions', label: 'Questions' },
  { key: 'requests', label: 'Requests' },
  { key: 'notebook', label: 'Notebook' },
  { key: 'ai', label: 'AI' },
];

/**
 * Pear AppShell — React owns all rendering now.
 *
 * Replaces the hand-wired DOM in `index.html` + `main.ts`: showPair/showChat
 * become `status === 'pair'`, showTab becomes `tab` state (so the
 * `display:flex` vs `[hidden]` specificity bug that needed the
 * `[hidden] { display: none !important }` patch cannot occur), and the tab bar
 * carries one measured sliding indicator.
 */
export function App() {
  const [tab, setTab] = useState<Tab>('thread');
  const [token, setToken] = useState<string | null>(() => getToken());
  const [agentDismissed, setAgentDismissed] = useState(false);
  // Settings is a screen over the chat, not a sixth tab: opened from the
  // header, closed by Done or by picking a tab (card 0xhNPdS42FTh).
  const [settingsOpen, setSettingsOpen] = useState(false);
  const keyboardInset = useKeyboardInset();

  const sessionRef = useRef<ReturnType<typeof useSession> | null>(null);
  const session = useSession(() => sessionRef.current?.leave());
  sessionRef.current = session;

  const threadRef = useRef<ReturnType<typeof useThread> | null>(null);
  const thread = useThread({
    token,
    members: session.members,
    onInvalidToken: () => sessionRef.current?.leave(),
  });
  threadRef.current = thread;

  // Keep the socket token in step with the session lifecycle.
  useEffect(() => {
    setToken(session.status === 'chat' ? getToken() : null);
    if (session.status === 'pair') {
      setTab('thread');
      setSettingsOpen(false);
    }
  }, [session.status]);

  // Dropping back to the pair screen must clear the thread too.
  useEffect(() => {
    if (!token) threadRef.current?.reset();
  }, [token]);

  const onChat = session.status === 'chat';

  /** Leave is destructive and lives in Settings now — one place, one confirm. */
  const leaveCouple = () => {
    threadRef.current?.reset();
    session.leave();
    setSettingsOpen(false);
  };

  return (
    <div
      id="app"
      className="pear-app"
      style={{ ['--kb-inset' as string]: `${keyboardInset}px` }}
      data-react-shell="app"
    >
      <header className="pear-header">
        <div className="pear-header-inner">
          <span className="pear-mark" />
          <h1 className="pear-title">Pear</h1>
          <button
            className="pear-btn pear-btn-blush pear-header-btn"
            id="btn-settings"
            hidden={!onChat}
            aria-label="Settings"
            aria-expanded={settingsOpen}
            onClick={() => setSettingsOpen(open => !open)}
          >
            Settings
          </button>
        </div>
      </header>

      <main className="pear-main">
        {!onChat && (
          <Pair
            pendingCode={session.pendingCode}
            busy={session.busy}
            onCreate={name => void session.create(name)}
            onJoin={(code, name) => void session.join(code, name)}
          />
        )}

        {onChat && (
          <section className={`pear-chat fade-in`} id="chat-screen">
            {/* TabBar renders as a bottom bar — see App.tsx bottom nav. */}
            {/* The code used to disappear with the pair screen, leaving a
                creator with nothing to send their partner. */}
            {session.pendingCode && session.members.length < 2 && (
              <div className="pear-share-code" aria-label="Pair code">
                <span className="pear-share-code-label">Pair code — send this to your partner</span>
                <code className="pear-code">{session.pendingCode}</code>
              </div>
            )}

            {/* Panels mount only while their tab is active — the old shell
                kept all three in the DOM and lazily called mount() once.
                Settings replaces them while open; the tab bar stays so a tap
                on a tab is still a way out. */}
            {settingsOpen && (
              <div id="panel-settings" className="pear-panel">
                <Settings onClose={() => setSettingsOpen(false)} onLeave={leaveCouple} />
              </div>
            )}

            {!settingsOpen && tab === 'ai' && (
              <div id="panel-ai" className="pear-panel">
                <AIPanel />
              </div>
            )}
            {!settingsOpen && tab === 'notebook' && (
              <div id="panel-notebook" className="pear-panel">
                <NotebookPanel />
              </div>
            )}
            {!settingsOpen && tab === 'questions' && (
              <div id="panel-questions" className="pear-panel">
                <QuestionsPanel members={session.members} />
              </div>
            )}
            {!settingsOpen && tab === 'requests' && (
              <div id="panel-requests" className="pear-panel">
                <RequestsPanel />
              </div>
            )}

            {!settingsOpen && tab === 'thread' && (
              <Thread
                messages={thread.messages}
                echoes={thread.echoes}
                roleFor={thread.roleFor}
                myMemberId={thread.myMemberId}
                onSend={thread.send}
                loading={Boolean(token) && !thread.historyLoaded}
              />
            )}
          </section>
        )}
      </main>

      <aside className="pear-agent" id="agent-panel" hidden={agentDismissed}>
        <div className="pear-agent-card">
          <span className="pear-agent-dot" />
          <p className="pear-agent-copy">What would you like to remember tonight?</p>
          <button
            className="pear-agent-dismiss"
            aria-label="Dismiss prompt"
            onClick={() => setAgentDismissed(true)}
          >
            ✕
          </button>
        </div>
      </aside>

      {/* Bottom nav — last child so it sits in the thumb zone; sticky keeps
          it on screen while a panel scrolls (design review §5.5). */}
      {onChat && (
        <TabBar
          tabs={TABS}
          active={tab}
          onChange={k => {
            // Picking a tab is a way out of Settings.
            setSettingsOpen(false);
            setTab(k as Tab);
          }}
          id="pear-tabs"
        />
      )}
    </div>
  );
}
